/*
 Общее состояние для колонки табличного комментария: признак колонки и заявка на
 автоматический вход в правку. Заявка нужна, потому что колонку включает QueryTable,
 а редактирование открывает ячейка, которая появится только после обновления таблицы.
*/

import { ref } from "vue";
import { ColumnCategory } from "@/models/etc/types";
import type { LiteColumn } from "./types";

// Через сколько заявка считается просроченной: строка могла и не приехать в ответе.
const REQUEST_TTL_MS = 15000;

// Пока сохраняются настройки отображения с новой колонкой, таблица должна выглядеть
// занятой: запрос уходит раньше обновления данных, и без этого между исчезновением кнопки
// и появлением колонки был бы момент, когда таблица выглядит обычной.
export const commentColumnBusy = ref(false);

export const pendingCommentEditEntityId = ref<number | null>(null);
// Пока ширины колонок не зафиксированы, тело таблицы переезжает с обычного tbody на
// виртуальный и строки перемонтируются. Поэтому заявку открываем только по готовности,
// иначе правка успела бы начаться и тут же пропасть вместе со старой ячейкой.
export const commentEditArmed = ref(false);
let requestedAt = 0;

// Клик по другой строке во время правки только завершает текущую правку, а не переносит
// курсор в новую ячейку: иначе правка скачет по таблице от каждого промаха. Ячейка, чья
// правка закончилась, оставляет отметку, а ячейка, поймавшая следующий клик, её забирает.
// Явный клик по карандашу другой строки идёт мимо этой проверки и открывает правку сразу.
const EDIT_HANDOFF_MS = 400;
let editingEndedAt = 0;

export function markCommentEditingEnded() {
  editingEndedAt = Date.now();
}

export function consumeCommentEditingHandoff(): boolean {
  if (!editingEndedAt) return false;
  const fresh = Date.now() - editingEndedAt < EDIT_HANDOFF_MS;
  editingEndedAt = 0;
  return fresh;
}

export function isCommentColumn(col: LiteColumn): boolean {
  return !col.isMultiedit && !!col.categories?.includes(ColumnCategory.TableComments);
}

export function requestCommentEdit(entityId: number) {
  pendingCommentEditEntityId.value = entityId > 0 ? entityId : null;
  commentEditArmed.value = false;
  requestedAt = Date.now();
}

export function clearCommentEditRequest() {
  pendingCommentEditEntityId.value = null;
  commentEditArmed.value = false;
  requestedAt = 0;
}

// Таблица готова: колонка на месте, ширины зафиксированы, можно открывать правку.
export function armCommentEdit() {
  if (pendingCommentEditEntityId.value) commentEditArmed.value = true;
}

// Заявка ждёт именно эту запись и ещё не просрочена. Гасит её сама ячейка, когда сеанс
// правки закончился: до этого заявка должна пережить возможный перемонтаж строки.
export function isCommentEditRequested(entityId?: number): boolean {
  if (!entityId || !commentEditArmed.value) return false;
  if (pendingCommentEditEntityId.value !== entityId) return false;
  return Date.now() - requestedAt < REQUEST_TTL_MS;
}
